import { useMemo, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import { ChevronLeft, Download } from 'lucide-react'
import { useAuth } from '../hooks/useAuth'
import { useBudget } from '../hooks/useBudget'
import { useQuery } from '@tanstack/react-query'
import { supabase } from '../lib/supabase'
import { getBudgetStatus } from '../lib/budgetCalculator'

// ─── Helpers ──────────────────────────────────────────────────────────────────

function groupByDate(confirmations: any[]): Map<string, any[]> {
  const map = new Map<string, any[]>()
  for (const c of confirmations) {
    const key = new Date(c.confirmed_at).toLocaleDateString('en-US', {
      month: 'long', day: 'numeric',
    })
    if (!map.has(key)) map.set(key, [])
    map.get(key)!.push(c)
  }
  return map
}

function escapeCSV(val: string | number | null | undefined): string {
  if (val === null || val === undefined) return ''
  const s = String(val)
  if (s.includes(',') || s.includes('"') || s.includes('\n')) {
    return `"${s.replace(/"/g, '""')}"`
  }
  return s
}

// ─── Section eyebrow with dotted line + trailing meta ─────────────────────────

function SectionEyebrow({ label, meta }: { label: React.ReactNode; meta?: React.ReactNode }) {
  return (
    <div className="flex items-baseline gap-2 mt-6 mb-3">
      <h2 className="font-serif text-[14px] italic text-ink">{label}</h2>
      <div className="flex-1 h-px bg-border-sand" />
      {meta && (
        <span className="text-[10px] uppercase tracking-[0.08em] font-medium text-ink-muted">
          {meta}
        </span>
      )}
    </div>
  )
}

// ─── BudgetDetails ────────────────────────────────────────────────────────────

export default function BudgetDetails() {
  const navigate    = useNavigate()
  const { session } = useAuth()
  const userId      = session?.user.id

  const { data: userData } = useQuery({
    queryKey: ['userMeta', userId],
    enabled:  !!userId,
    queryFn:  async () => {
      const { data } = await supabase
        .from('users')
        .select('household_id')
        .eq('id', userId!)
        .single()
      return data
    },
  })

  const householdId = userData?.household_id ?? null

  useEffect(() => { document.title = 'Budget — Pantry' }, [])

  const { budget, summary, confirmations, period } = useBudget(householdId)

  const grouped = useMemo(() => groupByDate(confirmations), [confirmations])

  const totalSpent = confirmations.reduce(
    (s, c) => s + ((c.price_paid as number) || 0), 0,
  )

  const status = summary ? getBudgetStatus(summary) : null

  // ── CSV export ────────────────────────────────────────────────────────────

  function handleExport() {
    const rows = [
      ['Date', 'Item', 'Category', 'Store', 'Price'].join(','),
      ...confirmations.map(c => [
        escapeCSV(new Date(c.confirmed_at).toLocaleDateString('en-US')),
        escapeCSV((c.pantry_items as any)?.name ?? ''),
        escapeCSV((c.pantry_items as any)?.display_category ?? ''),
        escapeCSV(c.store_name ?? ''),
        escapeCSV(c.price_paid?.toFixed(2) ?? ''),
      ].join(',')),
    ]

    const csv  = rows.join('\n')
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
    const url  = URL.createObjectURL(blob)
    const a    = document.createElement('a')
    a.href     = url
    a.download = `pantry-budget-${period.label.toLowerCase().replace(' ', '-')}.csv`
    a.click()
    URL.revokeObjectURL(url)
  }

  // ── Render ────────────────────────────────────────────────────────────────

  if (!householdId) {
    return (
      <div className="min-h-screen bg-cream pb-24">
        <div className="max-w-[440px] mx-auto px-5 pt-3">
          <div className="h-5 w-28 rounded-lg bg-paper border border-border-sand animate-pulse mb-2" />
          <div className="h-7 w-48 rounded-lg bg-paper border border-border-sand animate-pulse mb-6" />
          <div className="h-32 rounded-3xl bg-paper border border-border-sand animate-pulse mb-3" />
          <div className="h-48 rounded-2xl bg-paper border border-border-sand animate-pulse" />
        </div>
      </div>
    )
  }

  // Tone for the big spend number — forest when under, terracotta when over.
  const overBudget = !!summary && !!budget && summary.spent > budget
  const spentTone = overBudget ? 'text-terracotta' : 'text-forest'
  const projectedOver = !!summary && !!budget && summary.projectedSpend > budget

  return (
    <main className="min-h-screen bg-cream pb-24">
      <div className="max-w-[440px] mx-auto px-5">

        {/* Header */}
        <div className="pt-3 pb-4 flex items-center gap-3">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="p-1.5 -ml-1.5 rounded-full text-ink"
            aria-label="Back"
          >
            <ChevronLeft className="h-5 w-5" strokeWidth={2} />
          </button>
          <div className="flex-1">
            <p className="text-[10px] uppercase tracking-[0.18em] text-ink-muted font-medium">
              Budget
            </p>
            <h1 className="font-serif text-[22px] text-ink leading-[1.15] tracking-[-0.015em] mt-0.5">
              {period.label.replace(/(\b\w+)$/, w => w)} <em className="italic">budget</em>
            </h1>
          </div>
          <button
            type="button"
            onClick={handleExport}
            className="flex items-center gap-1.5 rounded-full border border-border-sand bg-paper px-3 py-1.5 text-[11px] uppercase tracking-[0.08em] font-medium text-ink-muted"
          >
            <Download className="h-3 w-3" strokeWidth={2} />
            CSV
          </button>
        </div>

        {/* Summary card */}
        {summary && status && (
          <motion.div
            className="bg-paper border border-border-sand rounded-3xl p-5"
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.25 }}
          >
            <div className="flex items-baseline justify-between mb-1.5">
              <p className="text-[10px] uppercase tracking-[0.15em] text-ink-muted font-medium">
                Spent so far
              </p>
              <span
                className="text-[10px] uppercase tracking-[0.08em] font-medium"
                style={{ color: status.color }}
              >
                {status.label}
              </span>
            </div>

            <div className="flex items-baseline gap-2 mb-3">
              <span className={`font-serif text-[40px] leading-none tracking-[-0.02em] tabular-nums ${spentTone}`}>
                ${summary.spent.toFixed(2)}
              </span>
              {budget && (
                <span className="font-serif italic text-[13px] text-ink-muted">
                  of ${budget.toFixed(2)}
                </span>
              )}
            </div>

            {/* Progress bar */}
            <div className="h-2 bg-border-sand rounded-full overflow-hidden mb-4">
              <motion.div
                className={`h-full rounded-full ${overBudget ? 'bg-terracotta' : 'bg-forest'}`}
                initial={{ width: 0 }}
                animate={{ width: `${Math.min(100, summary.percentUsed)}%` }}
                transition={{ duration: 0.7, ease: 'easeOut' }}
              />
            </div>

            <div className="grid grid-cols-3 gap-3 pt-3 border-t border-border-hairline">
              <div>
                <p className="text-[10px] uppercase tracking-[0.12em] font-medium text-ink-muted">Remaining</p>
                <p className="font-serif text-[16px] font-medium text-ink mt-0.5 tabular-nums">
                  ${summary.remaining.toFixed(2)}
                </p>
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-[0.12em] font-medium text-ink-muted">Projected</p>
                <p className={`font-serif text-[16px] font-medium mt-0.5 tabular-nums ${projectedOver ? 'text-terracotta' : 'text-ink'}`}>
                  ${Math.round(summary.projectedSpend)}
                </p>
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-[0.12em] font-medium text-ink-muted">Days left</p>
                <p className="font-serif text-[16px] font-medium text-ink mt-0.5 tabular-nums">
                  {summary.daysLeft}
                </p>
              </div>
            </div>
          </motion.div>
        )}

        {/* Transactions */}
        {confirmations.length === 0 ? (
          <div className="bg-paper border border-border-sand rounded-2xl px-5 py-10 text-center mt-6">
            <p className="font-serif italic text-[15px] text-ink">No transactions yet.</p>
            <p className="font-serif text-[12px] italic text-ink-muted mt-1.5 max-w-[260px] mx-auto leading-[1.5]">
              Prices you log when confirming items will appear here.
            </p>
          </div>
        ) : (
          <>
            <SectionEyebrow
              label={<>Every <em className="italic">transaction</em></>}
              meta={`${confirmations.length} this period`}
            />

            {Array.from(grouped.entries()).map(([date, items], groupIdx) => (
              <div key={date} className="mb-4">
                <p className="font-serif italic text-[13px] text-ink-muted mb-2 px-1">{date}</p>
                <div className="bg-paper border border-border-sand rounded-2xl divide-y divide-border-hairline overflow-hidden">
                  {items.map((c, i) => (
                    <motion.div
                      key={c.id}
                      className="flex items-center gap-3 px-4 py-3"
                      initial={{ opacity: 0, x: -6 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: groupIdx * 0.04 + i * 0.02, duration: 0.2 }}
                    >
                      <div className="flex-1 min-w-0">
                        <p className="font-serif text-[14px] font-medium text-ink leading-tight truncate">
                          {c.pantry_items?.name ?? 'Unknown item'}
                        </p>
                        <div className="flex items-center gap-2 mt-0.5">
                          {c.pantry_items?.display_category && (
                            <span className="inline-block text-[10px] uppercase tracking-[0.08em] font-medium text-ink-muted">
                              {c.pantry_items.display_category}
                            </span>
                          )}
                          {c.store_name && (
                            <span className="font-serif italic text-[11px] text-ink-muted">
                              · {c.store_name}
                            </span>
                          )}
                        </div>
                      </div>
                      <span className="font-serif text-[14px] font-medium text-ink tabular-nums shrink-0">
                        ${(c.price_paid as number).toFixed(2)}
                      </span>
                    </motion.div>
                  ))}
                </div>
              </div>
            ))}

            {/* Running total card */}
            <div className="bg-paper border border-border-sand rounded-2xl px-5 py-4 flex items-center justify-between">
              <p className="text-[10px] uppercase tracking-[0.15em] text-ink-muted font-medium">
                Total {period.label.toLowerCase()}
              </p>
              <p className="font-serif text-[18px] font-medium text-ink tabular-nums">
                ${totalSpent.toFixed(2)}
              </p>
            </div>
          </>
        )}

      </div>
    </main>
  )
}
